/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useRetracked } from 'js/lib/retracked';

import { Button, CheckboxGroup, ContextualHelp, Search, Typography2, typography2 } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import useDebounce from 'bundles/common/hooks/useDebounce';
import { useEnterpriseAdminSearchContext } from 'bundles/enterprise-admin-product-search/context';
import { usePageConfigComponentTrackingData } from 'bundles/page-config-common/contexts/PageConfigComponentMetadataContext';
import TrackedButton from 'bundles/page/components/TrackedButton';
import CourseraPlusSearchFilter from 'bundles/search-common/components/filters/CourseraPlusSearchFilter';
import { SearchFilterItem } from 'bundles/search-common/components/filters/SearchFilterItem';
import {
  ENTERPRISE_COLLECTION_ENTITIES,
  SEARCHABLE_FILTER_GROUP_KEYS,
  SEARCH_MAIN_INDEX_ID,
  THRESHOLD_FOR_FILTER_GROUP_BECOMING_SEARCHABLE,
} from 'bundles/search-common/constants';
import { IS_PART_OF_COURSERA_PLUS } from 'bundles/search-common/constants/filter';
import { useSearch } from 'bundles/search-common/providers/SearchContext';
import type { SearchFilterEventingData, SearchFilterOption } from 'bundles/search-common/types';
import type { FacetConfig } from 'bundles/search-common/types/filter';
import useIsConsumerSerp from 'bundles/search-common/utils/useIsConsumerSerp';
import { getLanguageModeClarity, removeWhiteSpace } from 'bundles/search-common/utils/utils';

import _t from 'i18n!nls/search-common';

export const FILTER_PREVIEW_COUNT = 4;
type Props = {
  groupConfig: FacetConfig;
  groupValues: SearchFilterOption[];
  eventingData: SearchFilterEventingData;
  onFilterValueChange?: () => void;
  groupShouldHaveMinHeight?: boolean;
  showSupportText?: boolean;
};
const styles = {
  searchFilterGroup: css`
    position: relative;
    margin-bottom: 32px;
  `,
  groupSearch: css`
    margin-bottom: var(--cds-spacing-50);

    .cds-Search-inputRoot {
      min-width: 220px;
      width: 100%;
    }
  `,
  scrollableCheckboxGroup: css`
    overflow-y: auto;
  `,
  checkboxGroupItemsWrapper: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-50);
    padding-bottom: var(--cds-spacing-50);

    .cds-checkboxAndRadio-label {
      width: fit-content;
    }
  `,
  checkboxGroup: css`
    width: 100%;

    legend {
      border-bottom: none;
    }

    /* necessary to fix discrepancy between storybook and here */
    button.cds-ContextualHelp-helpToggleButton {
      bottom: -4px;
    }
  `,

  seeMoreButton: css`
    button {
      padding: 0;
      text-decoration: underline;
      color: var(--cds-color-neutral-primary);
      ${typography2.bodyPrimary}
      margin-top: var(--cds-spacing-100);
    }
  `,

  contextualHelp: css`
    margin-bottom: var(--cds-spacing-50);

    .cds-ContextualHelp-tooltipContainer {
      margin-bottom: var(--cds-spacing-100);
    }
  `,
  forceMinHeight: css`
    min-height: 300px;
  `,
};

export function filterFilterOptions(
  searchInputValue: string,
  isEnterpriseAdminSearch: boolean
): (value: SearchFilterOption, index: number, array: SearchFilterOption[]) => boolean {
  return (item) => {
    const itemMatchesSearchQuery =
      !searchInputValue || item.label.toLowerCase().includes(searchInputValue.toLowerCase());
    const productTypeIncludedInEnterprise =
      !isEnterpriseAdminSearch || (isEnterpriseAdminSearch && !ENTERPRISE_COLLECTION_ENTITIES.includes(item.label));
    return itemMatchesSearchQuery && productTypeIncludedInEnterprise;
  };
}

export const ExpandableSearchFilterGroup = (props: Props) => {
  const { groupConfig, groupValues = [], eventingData, onFilterValueChange, groupShouldHaveMinHeight } = props;
  const { addFilters, removeFilters } = useSearch();
  const isEnterpriseAdminSearch = useEnterpriseAdminSearchContext();
  const isConsumerSearch = useIsConsumerSerp();
  const groupShouldExpandCollapse = !groupConfig.showAllItems;
  const [shouldShowAllItems, setShouldShowAllItems] = React.useState(groupConfig.showAllItems);
  const [searchInputValue, setSearchInputValue] = React.useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = React.useState('');
  const track = useRetracked();
  const trackV3 = useTracker();
  const pageConfigComponentTrackingData = usePageConfigComponentTrackingData();

  const filterGroupName = groupConfig.getName();
  const filterGroupKey = groupConfig.property;

  const debouncedRefine = useDebounce(() => {
    setDebouncedSearchQuery(searchInputValue);
    track({
      action: 'search',
      trackingName: 'filter_query_change',
      trackingData: {
        newSearchValue: removeWhiteSpace(searchInputValue),
        filterCategory: filterGroupKey,
        page: window?.location.pathname,
      },
    });
  }, 250);

  const addFiltersToMainIndex = React.useCallback<(filter: string) => void>(
    (filter: string) => {
      addFilters(SEARCH_MAIN_INDEX_ID, [filter]);
      trackV3('apply_filter', {
        filter: [filter],
        filterSource: 'sidebar_filter',
        ...(pageConfigComponentTrackingData && {
          pageConfigComponent: {
            // Passing only the required properties to avoid bloating the event payload
            name: pageConfigComponentTrackingData?.name ?? '',
            index: pageConfigComponentTrackingData?.index ?? -1,
          },
        }),
      });

      onFilterValueChange?.();
    },
    [addFilters, onFilterValueChange, trackV3, pageConfigComponentTrackingData]
  );

  const removeFiltersFromMainIndex = React.useCallback<(filter: string) => void>(
    (filter: string) => {
      removeFilters(SEARCH_MAIN_INDEX_ID, [filter]);
      trackV3('apply_filter', {
        filter: [],
        filterSource: 'sidebar_filter',
        ...(pageConfigComponentTrackingData && {
          pageConfigComponent: {
            // Passing only the required properties here to avoid bloating the event payload
            name: pageConfigComponentTrackingData?.name ?? '',
            index: pageConfigComponentTrackingData?.index ?? -1,
          },
        }),
      });
      onFilterValueChange?.();
    },
    [removeFilters, onFilterValueChange, trackV3, pageConfigComponentTrackingData]
  );

  const onClear = React.useCallback<() => void>(() => {
    setDebouncedSearchQuery('');
    setShouldShowAllItems(false);
  }, []);

  const filteredGroupValues = React.useMemo(
    () => groupValues?.filter(filterFilterOptions(searchInputValue, isEnterpriseAdminSearch)),
    [groupValues, searchInputValue, isEnterpriseAdminSearch]
  );

  if (groupValues.length === 0) {
    return null;
  }

  if (filterGroupKey === IS_PART_OF_COURSERA_PLUS) {
    return (
      <CourseraPlusSearchFilter
        name={filterGroupName}
        items={groupValues}
        addFilters={addFiltersToMainIndex}
        removeFilters={removeFiltersFromMainIndex}
        eventingData={eventingData}
      />
    );
  }

  let renderLabel;
  if (groupConfig?.getToolTip?.()) {
    let tooltip = groupConfig?.getToolTip?.();
    if (isConsumerSearch && filterGroupKey === 'language') {
      if (getLanguageModeClarity([[filterGroupKey]])) {
        tooltip = _t(
          'Result will include both fully translated (audio, subtitles, course materials, etc) and partly translated contents.'
        );
      }
    }
    renderLabel = (labelComponent: React.ReactElement) => (
      <ContextualHelp
        helpToggleProps={{
          'aria-label': _t('Information about this filter group'),
        }}
        label={<h4>{labelComponent}</h4>}
        css={styles.contextualHelp}
        onChange={(isOpen) => {
          trackV3('click_button', {
            button: {
              name: 'contextual_help',
            },
            contextualHelp: {
              name: 'filter_group_help',
              isOpen,
            },
          });
        }}
      >
        <Typography2 component="p">{tooltip}</Typography2>
      </ContextualHelp>
    );
  } else {
    renderLabel = (labelComponent: React.ReactElement) => <h4>{labelComponent}</h4>;
  }

  const itemsPreviewList = filteredGroupValues?.slice(0, FILTER_PREVIEW_COUNT);

  // Check if we should show the search box based on original groupValues
  const shouldShowSearchBox =
    groupShouldExpandCollapse &&
    SEARCHABLE_FILTER_GROUP_KEYS.includes(filterGroupKey) &&
    groupValues.length > THRESHOLD_FOR_FILTER_GROUP_BECOMING_SEARCHABLE;

  // Only return null if there are no filtered results AND no search box should be shown
  if ((filteredGroupValues?.length ?? 0) === 0 && !shouldShowSearchBox) {
    return null;
  }

  return (
    <div
      css={[styles.searchFilterGroup, groupShouldHaveMinHeight && styles.forceMinHeight]}
      data-testid={`search-filter-group-${filterGroupName}`}
    >
      <CheckboxGroup label={filterGroupName} renderLabel={renderLabel} css={[styles.checkboxGroup]}>
        <>
          {/* only show search when relevant */}
          {shouldShowSearchBox && (
            <CheckboxGroup>
              {/* Need to wrap in CheckboxGroup to fix ACCESS-4097 */}
              <Search
                hideSearchButton
                placeholder={_t('Search #{filterGroupName} (#{count})', {
                  filterGroupName,
                  count: groupValues.length,
                })}
                value={searchInputValue}
                onChange={(value) => {
                  debouncedRefine();
                  setSearchInputValue(value);
                }}
                onClear={onClear}
                css={styles.groupSearch}
                clearButtonAriaLabel={_t('Clear #{filterGroupName} search', { filterGroupName })}
              />
            </CheckboxGroup>
          )}
        </>
        <div
          css={[
            styles.checkboxGroupItemsWrapper,
            groupShouldExpandCollapse && shouldShowAllItems && styles.scrollableCheckboxGroup,
          ]}
        >
          {/* No results */}
          {debouncedSearchQuery && (filteredGroupValues?.length ?? 0) === 0 && (
            <Typography2 component="p" variant="bodyPrimary" css={{ gridColumn: '1 / -1' }}>
              {_t('No results found')}
            </Typography2>
          )}
          {/* list of checkboxes */}
          {(shouldShowAllItems ? filteredGroupValues ?? [] : itemsPreviewList).map((filterItem) => {
            const { label, value, count, isRefined } = filterItem;
            return (
              <SearchFilterItem
                {...filterItem}
                label={label}
                key={label}
                value={`${filterGroupKey}:${value}`}
                isRefined={isRefined}
                count={count}
                filterCategoryName={filterGroupName}
                addFilters={addFiltersToMainIndex}
                removeFilters={removeFiltersFromMainIndex}
                isCompact={true}
                eventingData={eventingData}
                showSupportText={true}
              />
            );
          })}
        </div>
      </CheckboxGroup>

      {/* show "Show more" button if there are more than FILTER_PREVIEW_COUNT items and the group is not expanded */}
      {groupShouldExpandCollapse && (filteredGroupValues?.length ?? 0) > FILTER_PREVIEW_COUNT && (
        <div css={styles.seeMoreButton}>
          <Button
            data-testid="expand-filter-items-button"
            trackingName={`${shouldShowAllItems ? 'collapse' : 'expand'}_filter_items_button_${filterGroupKey}`}
            withVisibilityTracking={false}
            requireFullyVisible={false}
            component={TrackedButton}
            variant="ghost"
            onClick={() => setShouldShowAllItems(!shouldShowAllItems)}
            aria-label={_t('Show #{direction} #{filterGroupName} options', {
              filterGroupName,
              direction: shouldShowAllItems ? 'less' : 'more',
            })}
          >
            {shouldShowAllItems
              ? _t('Show less')
              : _t('Show #{countMore} more', { countMore: (filteredGroupValues?.length ?? 0) - FILTER_PREVIEW_COUNT })}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ExpandableSearchFilterGroup;
